/**
 * Utilitários para conversão e defaults do tamanho da etiqueta (matéria-prima).
 * Tamanho default alinhado ao backend: 100mm x 50mm = 10cm x 5cm (PdfController.generateEtiqueta).
 * DPI fixo 203 para conversão cm ↔ px (comum em impressoras de etiquetas).
 */

export const DEFAULT_DPI = 203

/** Largura padrão em cm (equivalente a 100mm no backend). NÃO ALTERAR. */
export const DEFAULT_LABEL_WIDTH_CM = 10
/** Altura padrão em cm (equivalente a 50mm no backend). NÃO ALTERAR. */
export const DEFAULT_LABEL_HEIGHT_CM = 5

/**
 * Converte centímetros para pixels.
 * Fórmula: px = (cm / 2.54) * dpi
 * @param cm valor em centímetros
 * @param dpi resolução (default 203)
 * @returns pixels arredondados (inteiro)
 */
export function cmToPx(cm: number, dpi: number = DEFAULT_DPI): number {
  return Math.round((cm / 2.54) * dpi)
}

/**
 * Converte pixels para centímetros.
 * Fórmula: cm = (px / dpi) * 2.54
 * @param px valor em pixels
 * @param dpi resolução (default 203)
 * @returns valor em cm (pode ser decimal)
 */
export function pxToCm(px: number, dpi: number = DEFAULT_DPI): number {
  return (px / dpi) * 2.54
}

/** Retorna o tamanho default da etiqueta em pixels (não alterar esses valores). */
export function getDefaultLabelSizePx() {
  return {
    widthPx: cmToPx(DEFAULT_LABEL_WIDTH_CM),
    heightPx: cmToPx(DEFAULT_LABEL_HEIGHT_CM),
  }
}

export interface LabelSize {
  widthPx: number
  heightPx: number
  widthCm: number
  heightCm: number
}

/** Tamanho default completo (cm + px). Usado como estado inicial e ao cancelar. */
export function getDefaultLabelSize(): LabelSize {
  const widthPx = cmToPx(DEFAULT_LABEL_WIDTH_CM)
  const heightPx = cmToPx(DEFAULT_LABEL_HEIGHT_CM)
  return {
    widthPx,
    heightPx,
    widthCm: DEFAULT_LABEL_WIDTH_CM,
    heightCm: DEFAULT_LABEL_HEIGHT_CM,
  }
}

const STORAGE_KEY_LABEL_CM = 'label_size_cm'

/** Retorna o tamanho da etiqueta salvo (localStorage) ou o default. */
export function getCurrentLabelSize(): LabelSize {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LABEL_CM)
    if (!raw) return getDefaultLabelSize()
    const parsed = JSON.parse(raw) as { widthCm?: number; heightCm?: number }
    const w = Number(parsed?.widthCm)
    const h = Number(parsed?.heightCm)
    if (Number.isFinite(w) && Number.isFinite(h) && w >= 1 && w <= 30 && h >= 1 && h <= 30) {
      return {
        widthPx: cmToPx(w),
        heightPx: cmToPx(h),
        widthCm: w,
        heightCm: h,
      }
    }
  } catch {
    // ignore
  }
  return getDefaultLabelSize()
}

/** Converte posição em px (relativa à etiqueta) para mm para a API. */
export function labelPxToMm(
  xPx: number,
  yPx: number,
  size: LabelSize
): { x_mm: number; y_mm: number } {
  const widthMm = size.widthCm * 10
  const heightMm = size.heightCm * 10
  return {
    x_mm: (xPx / size.widthPx) * widthMm,
    y_mm: (yPx / size.heightPx) * heightMm,
  }
}
